import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import Form from "src/components/base/form";
import { useForm, useStore } from "@tanstack/react-form";
import React from "react";
import { Description, Field, Fieldset, Label } from "src/components/base/fieldset";
import { Divider } from "src/components/base/divider";
import { Button, PrimaryButton } from "src/components/base/button";
import { ApiInternal } from "src/api/api_internal";
import BlockUnsavedChanges from "src/components/base/unsaved-changes";
import { Listbox, ListboxDescription, ListboxLabel, ListboxOption } from "src/components/base/listbox";
import { LocalAuthRequirements } from "src/api/generated/frontend_internal_v1";
import { toast } from "react-toastify/unstyled";

/**
 * The properties for {@link SettingLocal}
 */
export type SettingLocalProps = {};

/**
 * Setting page to modify local user authentication policies
 */
function SettingLocal(props: SettingLocalProps) {
    const [t] = useTranslation("admin-settings");
    const [tg] = useTranslation();

    const router = useRouter();
    const data = Route.useLoaderData();

    const form = useForm({
        defaultValues: {
            requirements: data.requirements,
        },
        onSubmit: async ({ value }) => {
            await ApiInternal.settings.setLocalAuthRequirements(value.requirements);

            toast.success(tg("toast.saved-successfully"));
            router.invalidate();
            form.reset();
        },
    });
    const isDirty = useStore(form.store, (store) => store.isDirty);

    return (
        <div className={"flex flex-col justify-start gap-4"}>
            <Form className={"flex max-w-lg flex-col gap-8"} onSubmit={form.handleSubmit}>
                <Fieldset>
                    <form.Field name={"requirements"}>
                        {(fieldApi) => (
                            <Field>
                                <Label>{t("label.local-auth-requirements")}</Label>
                                <Description>{t("description.local-auth-requirements")}</Description>
                                <Listbox<LocalAuthRequirements>
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e)}
                                >
                                    <ListboxOption value={LocalAuthRequirements.None}>
                                        <ListboxLabel>{t("label.local-auth-requirements-none")}</ListboxLabel>
                                        <ListboxDescription>
                                            {t("description.local-auth-requirements-none")}
                                        </ListboxDescription>
                                    </ListboxOption>
                                    <ListboxOption value={LocalAuthRequirements.MultiFactor}>
                                        <ListboxLabel>{t("label.local-auth-requirements-multifactor")}</ListboxLabel>
                                        <ListboxDescription>
                                            {t("description.local-auth-requirements-multifactor")}
                                        </ListboxDescription>
                                    </ListboxOption>
                                    <ListboxOption value={LocalAuthRequirements.Passwordless}>
                                        <ListboxLabel>{t("label.local-auth-requirements-passwordless")}</ListboxLabel>
                                        <ListboxDescription>
                                            {t("description.local-auth-requirements-passwordless")}
                                        </ListboxDescription>
                                    </ListboxOption>
                                </Listbox>
                            </Field>
                        )}
                    </form.Field>
                </Fieldset>
                <Divider />
                <div className={"flex justify-end gap-3"}>
                    <Button plain={true} disabled={!isDirty} onClick={() => form.reset()}>
                        {t("button.reset")}
                    </Button>
                    <PrimaryButton type="submit">{t("button.save")}</PrimaryButton>
                </div>
            </Form>
            <BlockUnsavedChanges condition={isDirty} />
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/settings/_settings/local")({
    component: SettingLocal,
    loader: async () => await ApiInternal.settings.getLocalAuthRequirements(),
});
